import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { searchJobs, toggleJobLike } from "../services/jobsService";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";

export const useServiceRequestSearch = (pageSize = 10) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize from URL params
    const params = new URLSearchParams(location.search);
    const initialPage = parseInt(params.get("page") || "1");
    const initialSearch = params.get("search") || "";

    const [jobs, setJobs] = useState([]);
    const [nextJobs, setNextJobs] = useState(null);
    const [totalCount, setTotalCount] = useState(0);

    const [page, setPage] = useState(initialPage);
    const [search, setSearch] = useState(initialSearch);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [likedJobs, setLikedJobs] = useState({});
    const [feedbackJob, setFeedbackJob] = useState(null);
    const [hoveredJobId, setHoveredJobId] = useState(null);

    const { accessToken, role } = useAuth();

    // Format helpers
    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });

    const formatBudget = (job) => {
        const budget = parseFloat(job.budget);
        if (isNaN(budget)) return "TBD";
        return budget % 1 === 0 ? budget.toFixed(0) : budget.toFixed(2);
    };

    const normalizeJobImages = (jobs) => {
        return jobs.map(job => {
            const img = job.images?.[0];

            const safeImage =
                img?.image_200x150 ||
                img?.image_120 ||
                img?.image ||
                null;

            return {
                ...job,
                safeImage
            };
        });
    };

    const handleSearch = (newSearch) => {
        setSearch(newSearch);         // update state
        setPage(1);                   // reset page
        updateUrl(newSearch, 1, false); // update URL
    };

    // Sync state if URL changes manually or back/forward
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlSearch = params.get("search") || "";
        const urlPage = parseInt(params.get("page") || "1");

        if (urlSearch !== search) setSearch(urlSearch);
        if (urlPage !== page) setPage(urlPage);

    }, [location.search]);

    const updateUrl = (newSearch = search, newPage = page, replace = false) => {
        const params = new URLSearchParams();
        if (newSearch) params.set("search", newSearch);
        if (newPage > 1) params.set("page", newPage);
        navigate({ search: params.toString() }, { replace });
    };

    // Fetch jobs whenever search or page changes
    useEffect(() => {
        const fetchJobs = async () => {
            setLoading(true);
            setError(null);

            try {
                const validPage = Math.max(page, 1);
                const data = await searchJobs({
                    query: search,
                    page: validPage,
                    pageSize
                });

                const rawJobs = data.results || data;

                const cleanedJobs = normalizeJobImages(rawJobs);

                setJobs(cleanedJobs);
                setTotalCount(data.count || data.length);

            } catch (err) {
                setError(err?.response?.data?.detail || "Failed to load jobs.");
                setJobs([]);
                setTotalCount(0);
            } finally {
                setLoading(false);
            }
        };
        fetchJobs();
    }, [search, page, pageSize]);

    // Apply fetched jobs
    useEffect(() => {
        if (nextJobs) {
            setJobs(nextJobs);
            setNextJobs(null);
        }
    }, [nextJobs]);

    // Initialize likes
    useEffect(() => {
        if (jobs.length > 0) {
            const initialLikes = {};
            jobs.forEach(job => {
                initialLikes[job.id] = job.is_liked || false;
            });
            setLikedJobs(initialLikes);
        }
    }, [jobs]);

    // Toggle like
    const handleToggleLike = async (jobId) => {

        if (!accessToken || !role) {
            toast.info("Login or Sign Up to like");
            return;
        }

        try {
            const nowLiked = !likedJobs[jobId];

            setFeedbackJob({ id: jobId, text: nowLiked ? "Liked" : "Unliked" });
            setLikedJobs(prev => ({ ...prev, [jobId]: nowLiked }));

            await toggleJobLike(jobId);
        } catch (err) {
            console.error(err);
            // rollback optimistic update
            setLikedJobs(prev => ({ ...prev, [jobId]: !prev[jobId] }));
        } finally {
            setTimeout(() => setFeedbackJob(null), 1000);
        }
    };

    const goToPage = (pageNumber) => {
        setPage(pageNumber);
        updateUrl(search, pageNumber, false);
    };

    return {
        jobs,
        loading,
        error,
        search,
        setSearch,
        page,
        setPage,
        totalCount,
        pageSize,
        formatDate,
        formatBudget,
        likedJobs,
        feedbackJob,
        hoveredJobId,
        setHoveredJobId,
        handleToggleLike,
        goToPage,
        handleSearch
    };
};